import { useState, useEffect } from 'react';
import Navigation from '../../home/components/Navigation';
import Footer from '../../home/components/Footer';

export default function RetirementContribution() {
  useEffect(() => {
    document.title = "Retirement Contribution Calculator 2025 | 401k IRA Tax Savings - ItsJustTax";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Calculate retirement contribution tax savings for 401k, IRA, and other retirement accounts. Estimate tax deductions and benefits from retirement contributions. Maximize your retirement savings and reduce taxable income for 2025.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'retirement contribution calculator, 401k calculator, IRA tax deduction, retirement tax savings, 401k tax benefits');
    }
  }, []);

  const [age, setAge] = useState('');
  const [income, setIncome] = useState('');
  const [currentSavings, setCurrentSavings] = useState('');
  const [employerMatch, setEmployerMatch] = useState('');
  const [accountType, setAccountType] = useState('401k');
  const [result, setResult] = useState<any>(null);
  const [scrolled, setScrolled] = useState(false);

  const contributionLimits2026: Record<string, { standard: number; catchup: number }> = {
    '401k': { standard: 23000, catchup: 7500 },
    'ira': { standard: 7000, catchup: 1000 },
    'sep': { standard: 69000, catchup: 0 },
    'simple': { standard: 16000, catchup: 3500 }
  };

  const calculate = () => {
    const userAge = parseInt(age);
    const annualIncome = parseFloat(income);
    const savings = parseFloat(currentSavings) || 0;
    const match = parseFloat(employerMatch) || 0;

    if (isNaN(userAge) || isNaN(annualIncome) || userAge < 18) {
      alert('Please enter valid age and income');
      return;
    }

    const limits = contributionLimits2026[accountType];
    const isCatchupEligible = userAge >= 50;
    const maxContribution = limits.standard + (isCatchupEligible ? limits.catchup : 0);
    
    // Calculate recommended contribution (15% of income)
    const recommendedContribution = Math.min(annualIncome * 0.15, maxContribution);
    
    // Calculate employer match benefit
    const matchPercentage = match / 100;
    const employerContribution = Math.min(annualIncome * matchPercentage, maxContribution);
    
    // Tax savings calculation (assuming 22% tax bracket)
    const taxSavings = recommendedContribution * 0.22;
    
    // Total annual contribution
    const totalAnnualContribution = recommendedContribution + employerContribution;
    
    // Monthly contribution
    const monthlyContribution = recommendedContribution / 12;
    
    // Future value projection (30 years, 7% return)
    const years = Math.max(1, 65 - userAge);
    const rate = 0.07;
    const futureValue = savings * Math.pow(1 + rate, years) + 
      totalAnnualContribution * ((Math.pow(1 + rate, years) - 1) / rate);

    setResult({
      maxContribution,
      recommendedContribution,
      monthlyContribution,
      employerContribution,
      totalAnnualContribution,
      taxSavings,
      futureValue,
      years,
      isCatchupEligible,
      accountType
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation scrolled={scrolled} />
      
      <div className="pt-32 pb-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="mb-8">
            <a href="/" className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 mb-4 cursor-pointer">
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </a>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Retirement Contribution Calculator</h1>
            <p className="text-lg text-gray-600">Plan your retirement savings and maximize tax benefits for 2026</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Your Information</h2>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Your Age</label>
                  <input
                    type="number"
                    value={age}
                    onChange={(e) => setAge(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none text-lg"
                    placeholder="35"
                    min="18"
                    max="100"
                  />
                  <p className="text-sm text-gray-500 mt-1">Age 50+ qualifies for catch-up contributions</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Annual Income</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg">$</span>
                    <input
                      type="number"
                      value={income}
                      onChange={(e) => setIncome(e.target.value)}
                      className="w-full pl-8 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none text-lg"
                      placeholder="80000"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Current Retirement Savings</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg">$</span>
                    <input
                      type="number"
                      value={currentSavings}
                      onChange={(e) => setCurrentSavings(e.target.value)}
                      className="w-full pl-8 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none text-lg"
                      placeholder="50000"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Account Type</label>
                  <select
                    value={accountType}
                    onChange={(e) => setAccountType(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none text-lg cursor-pointer"
                  >
                    <option value="401k">401(k) / 403(b)</option>
                    <option value="ira">Traditional IRA</option>
                    <option value="sep">SEP IRA</option>
                    <option value="simple">SIMPLE IRA</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Employer Match (%)</label>
                  <div className="relative">
                    <input
                      type="number"
                      value={employerMatch}
                      onChange={(e) => setEmployerMatch(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none text-lg"
                      placeholder="4"
                      min="0"
                      max="100"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg">%</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">Percentage of salary your employer matches</p>
                </div>

                <button
                  onClick={calculate}
                  className="w-full py-4 bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-xl font-semibold hover:from-teal-700 hover:to-teal-800 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Calculate Retirement Plan
                </button>
              </div>
            </div>

            <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-8 shadow-lg text-white">
              <h2 className="text-2xl font-bold mb-6">Retirement Plan</h2>
              
              {result ? (
                <div className="space-y-6">
                  <div className="bg-white/10 rounded-xl p-6">
                    <div className="text-sm text-teal-100 mb-1">Recommended Monthly Contribution</div>
                    <div className="text-4xl font-bold">${result.monthlyContribution.toLocaleString(undefined, {maximumFractionDigits: 2})}</div>
                    <div className="text-sm text-teal-100 mt-2">
                      ${result.recommendedContribution.toLocaleString(undefined, {maximumFractionDigits: 2})} annually
                    </div>
                  </div>

                  {result.isCatchupEligible && (
                    <div className="bg-green-500/20 rounded-xl p-4 border-2 border-green-400/30">
                      <div className="flex items-center gap-2 mb-1">
                        <i className="ri-gift-line text-xl"></i>
                        <span className="font-semibold">Catch-Up Eligible!</span>
                      </div>
                      <p className="text-green-100 text-sm">
                        You can contribute an extra ${contributionLimits2026[result.accountType].catchup.toLocaleString()} as catch-up
                      </p>
                    </div>
                  )}

                  <div className="space-y-4">
                    <div className="flex justify-between items-center pb-3 border-b border-white/20">
                      <span className="text-teal-100">2026 Contribution Limit</span>
                      <span className="font-semibold">${result.maxContribution.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center pb-3 border-b border-white/20">
                      <span className="text-teal-100">Your Contribution</span>
                      <span className="font-semibold">${result.recommendedContribution.toLocaleString(undefined, {maximumFractionDigits: 2})}</span>
                    </div>
                    <div className="flex justify-between items-center pb-3 border-b border-white/20">
                      <span className="text-teal-100">Employer Match</span>
                      <span className="font-semibold text-green-300">+${result.employerContribution.toLocaleString(undefined, {maximumFractionDigits: 2})}</span>
                    </div>
                    <div className="flex justify-between items-center pb-3 border-b border-white/20">
                      <span className="text-teal-100">Total Annual</span>
                      <span className="font-semibold">${result.totalAnnualContribution.toLocaleString(undefined, {maximumFractionDigits: 2})}</span>
                    </div>
                    <div className="flex justify-between items-center pb-3 border-b border-white/20">
                      <span className="text-teal-100">Tax Savings</span>
                      <span className="font-semibold text-green-300">${result.taxSavings.toLocaleString(undefined, {maximumFractionDigits: 2})}</span>
                    </div>
                  </div>

                  <div className="bg-white/10 rounded-xl p-6 mt-6">
                    <div className="text-sm text-teal-100 mb-1">Projected Value at Age 65</div>
                    <div className="text-3xl font-bold mb-2">${result.futureValue.toLocaleString(undefined, {maximumFractionDigits: 0})}</div>
                    <p className="text-teal-100 text-sm">
                      Based on {result.years} years of contributions with 7% annual return
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center text-teal-100">
                    <i className="ri-funds-line text-6xl mb-4 opacity-50"></i>
                    <p>Enter your information to calculate retirement contributions</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="mt-12 bg-white rounded-2xl p-8 shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">2026 Contribution Limits</h3>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="border-2 border-gray-200 rounded-xl p-6">
                <h4 className="font-semibold text-gray-900 mb-3">401(k) / 403(b)</h4>
                <div className="text-3xl font-bold text-teal-600 mb-2">$23,000</div>
                <div className="text-sm text-gray-600 mb-3">Standard limit</div>
                <div className="text-sm text-gray-600">
                  <strong>+$7,500</strong> catch-up (age 50+)
                </div>
              </div>

              <div className="border-2 border-gray-200 rounded-xl p-6">
                <h4 className="font-semibold text-gray-900 mb-3">Traditional IRA</h4>
                <div className="text-3xl font-bold text-teal-600 mb-2">$7,000</div>
                <div className="text-sm text-gray-600 mb-3">Standard limit</div>
                <div className="text-sm text-gray-600">
                  <strong>+$1,000</strong> catch-up (age 50+)
                </div>
              </div>

              <div className="border-2 border-gray-200 rounded-xl p-6">
                <h4 className="font-semibold text-gray-900 mb-3">SEP IRA</h4>
                <div className="text-3xl font-bold text-teal-600 mb-2">$69,000</div>
                <div className="text-sm text-gray-600 mb-3">Standard limit</div>
                <div className="text-sm text-gray-600">
                  25% of compensation
                </div>
              </div>

              <div className="border-2 border-gray-200 rounded-xl p-6">
                <h4 className="font-semibold text-gray-900 mb-3">SIMPLE IRA</h4>
                <div className="text-3xl font-bold text-teal-600 mb-2">$16,000</div>
                <div className="text-sm text-gray-600 mb-3">Standard limit</div>
                <div className="text-sm text-gray-600">
                  <strong>+$3,500</strong> catch-up (age 50+)
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 shadow-lg text-white">
            <h3 className="text-2xl font-bold mb-6">Retirement Savings Tips</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex gap-4">
                <i className="ri-gift-line text-3xl flex-shrink-0"></i>
                <div>
                  <h4 className="font-semibold mb-2">Maximize Employer Match</h4>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    Always contribute enough to get the full employer match - it's free money that can significantly boost your retirement savings.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <i className="ri-time-line text-3xl flex-shrink-0"></i>
                <div>
                  <h4 className="font-semibold mb-2">Start Early</h4>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    The power of compound interest means starting early can make a huge difference in your retirement savings over time.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <i className="ri-arrow-up-line text-3xl flex-shrink-0"></i>
                <div>
                  <h4 className="font-semibold mb-2">Increase Contributions</h4>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    Try to increase your contribution rate by 1% each year, especially when you get a raise.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <i className="ri-pie-chart-line text-3xl flex-shrink-0"></i>
                <div>
                  <h4 className="font-semibold mb-2">Diversify Investments</h4>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    Spread your retirement savings across different asset classes to manage risk and optimize returns.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}